import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { Camera, X, RefreshCw, Aperture } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CameraCapture = ({
  onFileSelect,
  disabled = false,
  className = '',
  buttonLabel,
  facingMode = 'user',
}) => {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [streamError, setStreamError] = useState('');
  const [currentFacingMode, setCurrentFacingMode] = useState(facingMode);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const hasCameraSupport = useMemo(() => {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  }, []);

  const label = buttonLabel || t('admin.camera_take_photo');

  const stopStream = () => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
  };

  const closeModal = () => {
    setOpen(false);
    setStreamError('');
    stopStream();
  };

  const startCamera = async () => {
    if (!hasCameraSupport) {
      toast.error('Camera is not supported on this device/browser.');
      return;
    }
    setStreamError('');
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: currentFacingMode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      const message =
        err?.name === 'NotAllowedError'
          ? 'Camera permission denied. Please allow access and try again.'
          : err?.name === 'NotFoundError'
            ? 'No camera device found.'
            : 'Failed to start camera. Please try again.';
      setStreamError(message);
    } finally {
      setStarting(false);
    }
  };

  const flipCamera = async () => {
    stopStream();
    const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
    setCurrentFacingMode(newFacingMode);
  };

  useEffect(() => {
    if (!open) return;
    startCamera();
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentFacingMode]);

  const capture = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const w = video.videoWidth || 0;
    const h = video.videoHeight || 0;
    if (!w || !h) {
      toast.error('Camera is not ready yet. Please try again.');
      return;
    }

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      toast.error('Unable to capture image.');
      return;
    }

    ctx.drawImage(video, 0, 0, w, h);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
    });

    if (!blob) {
      toast.error('Capture failed. Please try again.');
      return;
    }

    const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });

    if (typeof onFileSelect === 'function') {
      onFileSelect({ target: { files: [file] } });
    }

    closeModal();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          if (!hasCameraSupport) {
            toast.error('Camera is not supported on this device/browser.');
            return;
          }
          setOpen(true);
        }}
        disabled={disabled}
        className={className}
      >
        <Camera className="h-4 w-4" />
        {label}
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-zinc-200 bg-white text-zinc-900 shadow-2xl overflow-hidden max-h-[92vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                <div className="font-semibold">{t('admin.camera_modal_title')}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={flipCamera}
                  disabled={starting}
                  className="h-9 w-9 inline-flex items-center justify-center rounded-lg !opacity-100 !bg-zinc-100 hover:!bg-zinc-200 !text-zinc-900 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Flip camera"
                  title={`Switch to ${currentFacingMode === 'user' ? 'rear' : 'front'} camera`}
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="h-9 w-9 inline-flex items-center justify-center rounded-lg !opacity-100 !bg-zinc-900 !text-white hover:!bg-zinc-800 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                  aria-label="Close camera"
                >
                  <X className="h-5 w-5 !text-white" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="relative rounded-xl overflow-hidden bg-zinc-100 aspect-[3/4] sm:aspect-video border border-zinc-200">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                  autoPlay
                />
                {starting && (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-700 bg-white/60">
                    {t('admin.camera_starting')}
                  </div>
                )}
                {!!streamError && (
                  <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-red-700 bg-white/80">
                    <div className="space-y-2">
                      <div className="font-semibold">{t('admin.camera_error')}</div>
                      <div className="text-zinc-700">{streamError}</div>
                      <button
                        type="button"
                        onClick={startCamera}
                        className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-900"
                      >
                        <RefreshCw className="h-4 w-4" />
                        {t('admin.camera_retry')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="min-w-24 px-4 py-2 rounded-lg !opacity-100 !bg-zinc-900 !text-white hover:!bg-zinc-800 text-sm font-semibold text-center shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                >
                  {t('admin.camera_cancel')}
                </button>
                <button
                  type="button"
                  onClick={capture}
                  disabled={starting || !!streamError}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg !bg-zinc-900 !text-white hover:!bg-zinc-800 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                >
                  <Aperture className="h-4 w-4" />
                  {t('admin.camera_capture')}
                </button>
              </div>
            </div>

            <canvas ref={canvasRef} className="hidden" />
          </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default CameraCapture;
